import { announce } from './announcer';

export type ToastVariant = 'success' | 'error';

export interface Toast {
  readonly id: string;
  readonly variant: ToastVariant;
  readonly message: string;
  /** `null` means sticky. Errors are always sticky. */
  readonly durationMs: number | null;
}

type Listener = (toasts: readonly Toast[]) => void;

const SUCCESS_DURATION_MS = 4_000;
/** Max 3 visible; the oldest is dropped. More than three on a 320px screen buries the CTA. */
const MAX_VISIBLE = 3;

let toasts: readonly Toast[] = [];
let nextId = 0;
const listeners = new Set<Listener>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener(toasts);
}

export function subscribeToToasts(listener: Listener): () => void {
  listeners.add(listener);
  listener(toasts);
  return () => {
    listeners.delete(listener);
  };
}

export function getToasts(): readonly Toast[] {
  return toasts;
}

function push(variant: ToastVariant, message: string): string {
  const id = `toast-${String(nextId++)}`;
  // Errors are sticky: a player on a bumpy bus will not read a four-second toast, and the error is
  // usually the only instruction they have.
  const durationMs = variant === 'error' ? null : SUCCESS_DURATION_MS;

  const dropped = toasts.length >= MAX_VISIBLE ? toasts[0] : undefined;
  const kept = dropped === undefined ? toasts : toasts.slice(1);
  if (dropped !== undefined) clearTimer(dropped.id);

  toasts = [...kept, { id, variant, message, durationMs }];
  emit();

  // Routed through the app's single polite region — the toast markup itself carries no `aria-live`
  // (ADR-015). An error is still polite: `assertive` is reserved for the expiry warning.
  announce(message, 'polite');

  if (durationMs !== null) {
    timers.set(
      id,
      setTimeout(() => {
        dismiss(id);
      }, durationMs),
    );
  }

  return id;
}

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

export function dismiss(id?: string): void {
  if (id === undefined) {
    for (const toast of toasts) clearTimer(toast.id);
    if (toasts.length === 0) return;
    toasts = [];
    emit();
    return;
  }

  clearTimer(id);
  const next = toasts.filter((toast) => toast.id !== id);
  // Identity is preserved when nothing was removed, so a stray dismiss cannot cause a re-render.
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

/**
 * The whole public surface. Feature code imports this rather than the store, so the sticky/auto
 * policy and the live-region routing are enforced in one file instead of per call site.
 *
 * Deliberately absent: `toast.info` and `toast.warning`. A status change is never a toast — it is a
 * view state (`Error-Handling.md`).
 *
 * Messages must already be localized. This module never calls `t`.
 */
export const toast = {
  success: (message: string): string => push('success', message),
  error: (message: string): string => push('error', message),
  dismiss,
} as const;

/** Test seam only. */
export function resetToastsForTests(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  toasts = [];
  nextId = 0;
  listeners.clear();
}
